import { proceduresDataset } from '../../workflow/connections'
import {
  WorkflowComponentCodeParams,
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentInput,
  WorkflowComponentOutput,
  WorkflowComponentParamType,
  WorkflowComponentOutputType
} from '../../types'
import { WorkflowComponent } from './workflowcomponent'

// A workflow component that is based on a stored procedure
export class StoredProcedureWorkflowComponent extends WorkflowComponent {
  orderedParameterNames: Array<string>
  version = '1'

  constructor(procedure: string) {
    const inputs: WorkflowComponentInput[] = []
    const outputs: WorkflowComponentOutput[] = []
    const header = procedure.match(/CREATE.*?PROCEDURE (.*?)\(([\S\s]*?)\)/)
    if (!header) {
      throw new Error('Invalid procedure definition')
    }
    const metadataComment = procedure.match(/\/\*([\S\s]*?)\*\//)
    let procMetadata: any = {}
    if (metadataComment) {
      try {
        procMetadata = JSON.parse(metadataComment[1])
      } catch (e) {
        throw new Error('Error parsing procedure metadata')
      }
    } else {
      throw new Error('Missing procedure metadata')
    }
    const procname = header[1].split('`').join('').split('.').pop()
    const args = header[2].split(',').map((x) => x.trim())
    const parameterNames: Array<string> = []
    const [lastArgName, lastArgType] = args[args.length - 1].split(' ')
    if (
      !['bool', 'boolean'].includes(lastArgType.toLowerCase()) ||
      lastArgName !== 'dryRun'
    ) {
      throw new Error('Missing or wrong dryRun parameter')
    }
    for (let i = 0; i < args.length - 1; i++) {
      const [name, _] = args[i].split(' ')
      const trimmedName = name.trim()
      if (trimmedName in procMetadata.outputs) {
        outputs.push({
          name: trimmedName,
          title: procMetadata.outputs[trimmedName].title ?? trimmedName,
          description: procMetadata.outputs[trimmedName].description ?? '',
          type: WorkflowComponentOutputType.Table
        })
      } else if (trimmedName in procMetadata.inputs) {
        const paramType =
          WorkflowComponentParamType[
            Object.entries(WorkflowComponentParamType).find(
              ([key, val]) => val === procMetadata.inputs[trimmedName].type
            )?.[0] as string
          ]
        if (!paramType) {
          throw new Error(
            `Wrong parameter type: ${procMetadata.inputs[trimmedName].type}`
          )
        }
        const input = { ...procMetadata.inputs[trimmedName] }
        input.trimmedName = trimmedName
        input.title ??= trimmedName
        input.description ??= ''
        inputs.push(input as WorkflowComponentInput)
      } else {
        throw new Error(`Missing metadata for parameter '${name}'`)
      }
      parameterNames.push(name)
    }
    super(
      {
        name: procname as string,
        group: procMetadata.group ?? 'Procedures',
        title: procMetadata.title ?? (procname as string),
        description: procMetadata.description ?? '',
        starred: procMetadata.starred,
        icon: procMetadata.icon
      },
      inputs,
      outputs
    )
    this.orderedParameterNames = parameterNames
    this.version = procMetadata.version ?? '1'
  }

  getVersion(provider: Provider): string {
    return this.version
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTables: Record<string, string> = {}
    for (const output of this.outputs) {
      const tablename = connection.getOutputTableName(
        workflow.id,
        componentName,
        params,
        output.name,
        codeType
      )
      outputTables[output.name] = tablename
    }
    const preparedParams = this._prepareParameters(params)
    const storedProcParams = { ...preparedParams, ...outputTables }
    const types = Object.fromEntries(
      [...this.inputs, ...this.outputs].map((x) => [x.name, x.type])
    )
    const paramsString = this.orderedParameterNames
      .map((x) => {
        if (
          [
            WorkflowComponentParamType.InputTable,
            WorkflowComponentParamType.OutputTable,
            WorkflowComponentParamType.String,
            WorkflowComponentParamType.Selection,
            WorkflowComponentParamType.Column,
            WorkflowComponentOutputType.Table
          ].includes(types[x])
        ) {
          return storedProcParams[x] ? `'''${storedProcParams[x]}'''` : 'null'
        } else {
          return storedProcParams[x] ?? 'null'
        }
      })
      .join(',\n')

    const query = `CALL \`${
      connection.tempStoragePath.split('.')[0]
    }.${proceduresDataset}\`.${this.metadata.name}(
      ${paramsString}, ${codeType === WorkflowCodeType.DryRun}
    );`
    return {
      query: query,
      outputs: outputTables
    }
  }
}
